import { Button, IconButton, SelectInput, SelectInputItem, Tag } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EDocumentationStatus } from '@/shared/api/generated/types';
import { useRejectionReasons } from '../../../hooks/useRejectionReasons';
import type { PersonAttachment } from '../../../types/attachment';

const STATUS_TAG_MAP = {
  pending: 'helper',
  approved: 'positive',
  approvedTemporary: 'highlight',
  rejected: 'negative',
} as const;

export interface UpdateAttachmentStatusSubmitData {
  readonly documentationStatus: EDocumentationStatus;
  readonly documentRejectionId?: number;
}

export interface UpdateAttachmentStatusModalProps {
  readonly personId: number;
  readonly attachment: PersonAttachment | null;
  readonly open: boolean;
  readonly isLoading?: boolean;
  /** Status pré-selecionado ao abrir (1=Aprovar, 2=Rejeitar, 3=Aprovação temporária). */
  readonly initialStatus?: EDocumentationStatus;
  readonly onClose: () => void;
  readonly onSubmit: (data: UpdateAttachmentStatusSubmitData) => void;
}

/**
 * Modal para atualizar o status de um anexo de matrícula.
 *
 * Exibe os três status possíveis (Aprovar, Aprovação Temporária, Rejeitar).
 * Ao selecionar Rejeitar, exibe o dropdown de motivos carregado da API.
 *
 * @param personId - ID da pessoa (necessário para carregar motivos de rejeição)
 * @param attachment - Anexo selecionado, ou `null` quando o modal está fechado
 * @param open - Controla visibilidade do modal
 * @param isLoading - Exibe spinner no botão de confirmar
 * @param onClose - Callback ao fechar
 * @param onSubmit - Callback com o novo status e, se rejeitado, o motivo
 */
export function UpdateAttachmentStatusModal({
  personId,
  attachment,
  open,
  isLoading = false,
  initialStatus,
  onClose,
  onSubmit,
}: UpdateAttachmentStatusModalProps) {
  const { t } = useTranslation('personManagement');

  // 1=Approved, 2=Rejected, 3=ApprovedTemporary
  const [selectedStatus, setSelectedStatus] = useState<EDocumentationStatus | null>(
    initialStatus ?? null,
  );
  const [selectedRejectionId, setSelectedRejectionId] = useState<number | undefined>(undefined);
  const [rejectionError, setRejectionError] = useState(false);

  const { options: rejectionReasons, isLoading: isLoadingReasons } = useRejectionReasons(personId);

  const isRejecting = selectedStatus === 2;

  // Sincroniza a seleção inicial sempre que o modal é aberto.
  useEffect(() => {
    if (open) {
      setSelectedStatus(initialStatus ?? null);
      setSelectedRejectionId(undefined);
      setRejectionError(false);
    }
  }, [open, initialStatus]);

  const resetForm = () => {
    setSelectedStatus(initialStatus ?? null);
    setSelectedRejectionId(undefined);
    setRejectionError(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleStatusSelect = (status: EDocumentationStatus) => {
    setSelectedStatus(status);
    setSelectedRejectionId(undefined);
    setRejectionError(false);
  };

  const handleConfirm = () => {
    if (selectedStatus === null) return;

    if (isRejecting && !selectedRejectionId) {
      setRejectionError(true);
      return;
    }

    onSubmit({
      documentationStatus: selectedStatus,
      ...(isRejecting && selectedRejectionId !== undefined
        ? { documentRejectionId: selectedRejectionId }
        : {}),
    });
  };

  return (
    <Dialog.Root open={open && attachment !== null} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {attachment && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
                <Dialog.Title className="ts-heading-sm text-ctx-content-title">
                  {t('updateStatusModal.title')}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <IconButton
                    icon={XMarkIcon}
                    hierarchy="tertiary"
                    size="sm"
                    ariaLabel={t('updateStatusModal.cancel')}
                  />
                </Dialog.Close>
              </div>

              {/* Body */}
              <div className="flex flex-col gap-xs overflow-y-auto px-xs py-mini">
                {/* Attachment info */}
                <div className="flex flex-col gap-quark rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
                  <div className="flex flex-col gap-quark">
                    <span className="ts-caption-xxs text-ctx-content-placeholder">
                      {t('updateStatusModal.fileName')}
                    </span>
                    <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                      {attachment.fileName}
                    </span>
                  </div>
                  {attachment.documentTypeName && (
                    <div className="flex flex-col gap-quark">
                      <span className="ts-caption-xxs text-ctx-content-placeholder">
                        {t('updateStatusModal.documentType')}
                      </span>
                      <span className="ts-paragraph-xs text-ctx-content-base">
                        {attachment.documentTypeName}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-xxs">
                    <span className="ts-caption-xxs text-ctx-content-placeholder">
                      {t('updateStatusModal.currentStatus')}
                    </span>
                    <Tag
                      label={t(`status.${attachment.documentStatus}`)}
                      size="sm"
                      status={STATUS_TAG_MAP[attachment.documentStatus]}
                      custom={{ text: 'text-nowrap' }}
                    />
                  </div>
                </div>

                {/* Status action buttons */}
                <div className="flex gap-xs">
                  <Button
                    hierarchy={selectedStatus === 1 ? 'primary' : 'secondary'}
                    size="md"
                    onClick={() => handleStatusSelect(1)}
                  >
                    {t('actions.approve')}
                  </Button>
                  <Button
                    hierarchy={selectedStatus === 3 ? 'primary' : 'secondary'}
                    size="md"
                    onClick={() => handleStatusSelect(3)}
                  >
                    {t('actions.approveTemporary')}
                  </Button>
                  <Button
                    hierarchy={selectedStatus === 2 ? 'danger' : 'secondary'}
                    size="md"
                    onClick={() => handleStatusSelect(2)}
                  >
                    {t('actions.reject')}
                  </Button>
                </div>

                {/* Rejection reason (conditional) */}
                {isRejecting && (
                  <div className="flex flex-col gap-quark">
                    <SelectInput
                      label={t('updateStatusModal.rejectionReason')}
                      size="md"
                      value={
                        selectedRejectionId !== undefined ? String(selectedRejectionId) : 'none'
                      }
                      onValueChange={v => {
                        setSelectedRejectionId(v === 'none' ? undefined : Number(v));
                        setRejectionError(false);
                      }}
                      disabled={isLoadingReasons}
                      hasError={rejectionError}
                      {...(rejectionError ? { helper: t('updateStatusModal.reasonRequired') } : {})}
                    >
                      <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                        {t('updateStatusModal.selectReason')}
                      </SelectInputItem>
                      {rejectionReasons.map(r => (
                        <SelectInputItem
                          key={r.id}
                          value={String(r.id)}
                          custom={{ root: 'text-nowrap' }}
                        >
                          {r.name}
                        </SelectInputItem>
                      ))}
                    </SelectInput>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-xs py-mini">
                <Button hierarchy="secondary" size="lg" onClick={handleClose} disabled={isLoading}>
                  {t('updateStatusModal.cancel')}
                </Button>
                <Button
                  hierarchy="primary"
                  size="lg"
                  onClick={handleConfirm}
                  isLoading={isLoading}
                  disabled={selectedStatus === null}
                >
                  {t('updateStatusModal.confirm')}
                </Button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
